import { z } from "zod";
import { ExtractionExpressionSchema } from "./extraction-expression";

/**
 * 证据事实、来源与字段采用（统一技术实施方案 §8、§5.4、§10.4）。
 *
 * 本文件只放**可以离开浏览器**的结构；页面原文、完整模型输出与截图字节留在本地（§11）。
 * 事实的"原始值 / 规则建议 / 模型推断 / 用户修改"必须可区分，靠 `origin` 与 `inferred` 两个字段，
 * 而不是靠调用方自觉。
 */

/* -------------------------------------------------------------------------- */
/* §10.4 统一错误码                                                             */
/* -------------------------------------------------------------------------- */

export const EVIDENCE_ERROR_CODES = [
  "EVIDENCE_NOT_AVAILABLE",
  "EXPRESSION_REJECTED",
  "EXTRACTION_LIMIT",
  "CONTROL_AMBIGUOUS",
  "CONTROL_UNSUPPORTED",
  "ACTION_POLICY_DENIED",
  "TARGET_CHANGED",
  "PERMIT_EXPIRED",
  "NO_PROGRESS",
  "BUDGET_EXHAUSTED",
  "SCREENSHOT_CONSENT_REQUIRED",
  "OUTPUT_INVALID",
  "EVIDENCE_REFERENCE_MISMATCH",
  "LOCAL_PERSISTENCE_FAILED",
  "CALL_INTERRUPTED",
  "ACTION_RESULT_UNKNOWN",
] as const;
export const EvidenceErrorCodeSchema = z.enum(EVIDENCE_ERROR_CODES);
export type EvidenceErrorCode = z.infer<typeof EvidenceErrorCodeSchema>;

/* -------------------------------------------------------------------------- */
/* §8.2 时间口径                                                                */
/* -------------------------------------------------------------------------- */

/**
 * `range` 是明确起止；`relative` 只保留原始描述；`snapshot` 是"此刻的取值"；
 * `unknown` 必须说明原因。**采集时间不能充当统计结束时间**，所以 `snapshot` 只有 observedAt，
 * 没有 end —— 想写结束日期就必须走 range 并给出真实起止。
 */
export const EvidenceTimeScopeSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("range"),
    start: z.string().date(),
    end: z.string().date(),
  }).strict().superRefine((value, context) => {
    if (value.start > value.end) context.addIssue({ code: "custom", path: ["start"], message: "起止时间顺序颠倒" });
  }),
  z.object({
    kind: z.literal("relative"),
    description: z.string().trim().min(1).max(120),
    /** 原始描述里能确定的自然日跨度；缺可靠边界时留空，不编绝对日期。 */
    days: z.number().int().min(1).max(3_650).optional(),
  }).strict(),
  z.object({
    kind: z.literal("snapshot"),
    observedAt: z.string(),
  }).strict(),
  z.object({
    kind: z.literal("unknown"),
    reason: z.string().trim().min(1).max(200),
  }).strict(),
]);
export type EvidenceTimeScope = z.infer<typeof EvidenceTimeScopeSchema>;

/** 精度分级见 §7.3：页面明示 > 用户确认的 OCR > 趋势方向 > 未知。 */
export const EvidencePrecisionSchema = z.enum(["exact", "confirmed_ocr", "approximate", "unknown"]);
export type EvidencePrecision = z.infer<typeof EvidencePrecisionSchema>;

export const EvidenceOriginSchema = z.enum(["page", "screenshot", "user", "file", "rule", "model_inference"]);
export type EvidenceOrigin = z.infer<typeof EvidenceOriginSchema>;

/* -------------------------------------------------------------------------- */
/* §5.4 来源记录                                                                */
/* -------------------------------------------------------------------------- */

const SafeSourceUrlSchema = z.string().max(2_048).refine((value) => {
  try {
    const url = new URL(value);
    if (url.protocol !== "https:") return false;
    if (url.username || url.password) return false;
    // §11.3：URL 清除凭据/敏感参数，允许保留已审核的查询/地区/时间参数。
    return ![...url.searchParams.keys()].some((key) => /token|password|secret|authorization|session|api.?key|cookie/i.test(key));
  } catch {
    return false;
  }
}, "只接受不含账号密码或访问令牌的 HTTPS 来源地址");

export const EvidenceLocationSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("block"), blockRef: z.string().min(1).max(160) }).strict(),
  z.object({ kind: z.literal("table_cell"), tableRef: z.string().min(1).max(160), row: z.number().int().min(1).max(2_000), column: z.number().int().min(1).max(80) }).strict(),
  z.object({ kind: z.literal("image_region"), imageId: z.string().min(1).max(160), bbox: z.object({ x: z.number(), y: z.number(), width: z.number().nonnegative(), height: z.number().nonnegative() }).strict() }).strict(),
  z.object({ kind: z.literal("user_input") }).strict(),
]);
export type EvidenceLocation = z.infer<typeof EvidenceLocationSchema>;

export const EvidenceSourceRefSchema = z.object({
  sourceRef: z.string().min(1).max(160),
  snapshotId: z.string().min(1).max(160),
  collectedAt: z.string(),
  sourceUrl: SafeSourceUrlSchema.optional(),
  /** 最终有效表达式：只有走页面表达式的来源才有。 */
  expression: ExtractionExpressionSchema.optional(),
  location: EvidenceLocationSchema,
  entityId: z.string().min(1).max(160).optional(),
  /** 采集时的筛选状态（如"马来西亚/女装/过去30天"），用于说明适用范围。 */
  filterState: z.string().max(300).optional(),
  precision: EvidencePrecisionSchema,
  /** 来源哈希用于检测内容变化，不等于平台认证。 */
  contentHash: z.string().regex(/^[a-f\d]{16,64}$/).optional(),
}).strict();
export type EvidenceSourceRef = z.infer<typeof EvidenceSourceRefSchema>;

/* -------------------------------------------------------------------------- */
/* §8.1 EvidenceFact / EvidenceBundle                                           */
/* -------------------------------------------------------------------------- */

export const EvidenceFactValueSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);

export const EvidenceFactSchema = z.object({
  factId: z.string().min(1).max(160),
  field: z.string().trim().min(1).max(120),
  value: EvidenceFactValueSchema,
  unit: z.string().trim().min(1).max(40).optional(),
  timeScope: EvidenceTimeScopeSchema,
  entityId: z.string().min(1).max(160).optional(),
  precision: EvidencePrecisionSchema,
  origin: EvidenceOriginSchema,
  /** 推断必须显式标记，且不与原始值混存。 */
  inferred: z.boolean().default(false),
  sourceRef: z.string().min(1).max(160).optional(),
  /** value 为 null 时说明缺什么；有值时不得用它掩盖无来源。 */
  missingReason: z.string().trim().min(1).max(200).optional(),
}).strict().superRefine((value, context) => {
  if (value.value === null && !value.missingReason) {
    context.addIssue({ code: "custom", path: ["missingReason"], message: "空值必须说明缺失原因" });
  }
  if (value.value !== null && value.missingReason) {
    context.addIssue({ code: "custom", path: ["missingReason"], message: "有值时不得声明缺失原因" });
  }
  if (value.origin === "model_inference" && !value.inferred) {
    context.addIssue({ code: "custom", path: ["inferred"], message: "模型推断必须标记 inferred" });
  }
  if (value.origin !== "model_inference" && value.inferred) {
    context.addIssue({ code: "custom", path: ["inferred"], message: "非推断来源不得标记 inferred" });
  }
  // 页面明示值与截图确认值都必须有出处。
  if (value.value !== null && (value.origin === "page" || value.origin === "screenshot") && !value.sourceRef) {
    context.addIssue({ code: "custom", path: ["sourceRef"], message: "页面或截图事实必须带来源" });
  }
  // 截图只能产生"用户确认的 OCR"精度，不能冒充页面明示值。
  if (value.origin === "screenshot" && value.precision !== "confirmed_ocr") {
    context.addIssue({ code: "custom", path: ["precision"], message: "截图来源必须标为 confirmed_ocr" });
  }
  if (value.origin === "page" && value.precision === "confirmed_ocr") {
    context.addIssue({ code: "custom", path: ["precision"], message: "页面明示值不应标为 OCR 精度" });
  }
});
export type EvidenceFact = z.infer<typeof EvidenceFactSchema>;

/** 分组件状态：一图有数据、另一组件暂无数据时分别记录，不整页清空（§8.3）。 */
export const EvidenceComponentStateSchema = z.object({
  componentRef: z.string().min(1).max(160),
  label: z.string().max(160).optional(),
  state: z.enum(["has_data", "empty", "not_loaded", "unreadable", "unsupported", "permission_denied", "budget_exhausted", "waiting_user"]),
  note: z.string().max(300).optional(),
  /** 读不到时的可读部分与缺失原因，不猜缺失值。 */
  partialFields: z.array(z.string().max(120)).max(40).default([]),
}).strict();
export type EvidenceComponentState = z.infer<typeof EvidenceComponentStateSchema>;

/** 覆盖范围：局部采集不得称为"全部"（§8.5）。 */
export const EvidenceCoverageSchema = z.object({
  scope: z.enum(["current_view", "specified_count", "filtered_result", "all_enumerated", "unknown"]),
  pagesRead: z.number().int().nonnegative().default(0),
  itemsRead: z.number().int().nonnegative().default(0),
  itemsDeduped: z.number().int().nonnegative().default(0),
  truncated: z.boolean().default(false),
  totalKnown: z.boolean().default(false),
  note: z.string().max(300).optional(),
}).strict().superRefine((value, context) => {
  if (value.scope === "all_enumerated" && (value.truncated || !value.totalKnown)) {
    context.addIssue({ code: "custom", path: ["scope"], message: "存在截断或总数未知时不得声明已全部枚举" });
  }
  if (value.itemsDeduped > value.itemsRead) {
    context.addIssue({ code: "custom", path: ["itemsDeduped"], message: "去重数量不能大于读取数量" });
  }
});
export type EvidenceCoverage = z.infer<typeof EvidenceCoverageSchema>;

/**
 * 目标市场口径（§8.2）：说明这批资料**服务哪个目标**，与"内容从哪来"分开。
 *
 * 来源侧继续由 `EvidenceSourceRef`（sourceUrl / snapshotId / filterState）负责；
 * 这里只写会话目标里的口径，**不从页面猜**——猜出来的国家/币种会让
 * V2 核实里的"目标市场 vs 来源市场"失去意义。
 *
 * 刻意不放 `sampleSize`：样本量已经由 `EvidenceCoverage.itemsRead` 表达，
 * 再加一个字段就是同一个数字两个所有者。
 *
 * 刻意不放"来源国家"：目前没有可靠读法（见方案 P0 探针待答问题），
 * 等探针给出稳定信号时，字段与生产者一起加，不留只写不读的死字段。
 */
export const EvidenceMarketSchema = z.object({
  platform: z.string().trim().min(1).max(80).optional(),
  country: z.string().trim().min(1).max(80).optional(),
  currency: z.string().trim().length(3).optional(),
  language: z.string().trim().min(1).max(35).optional(),
  category: z.string().trim().min(1).max(160).optional(),
}).strict().superRefine((value, context) => {
  if (!value.platform && !value.country && !value.currency && !value.language && !value.category) {
    context.addIssue({ code: "custom", path: ["country"], message: "目标市场口径至少要给出一项，不能是空对象" });
  }
});
export type EvidenceMarket = z.infer<typeof EvidenceMarketSchema>;

export const EvidenceEntitySchema = z.object({
  entityId: z.string().min(1).max(160),
  kind: z.enum(["product", "sku", "review", "supplier", "query", "page", "other"]),
  label: z.string().max(300).optional(),
  sourceRefs: z.array(z.string().min(1).max(160)).max(40).default([]),
}).strict();
export type EvidenceEntity = z.infer<typeof EvidenceEntitySchema>;

export const EvidenceConflictSchema = z.object({
  field: z.string().min(1).max(120),
  entityId: z.string().min(1).max(160).optional(),
  values: z.array(z.object({ value: EvidenceFactValueSchema, sourceRef: z.string().min(1).max(160) }).strict()).min(2).max(8),
  note: z.string().max(300).optional(),
}).strict();
export type EvidenceConflict = z.infer<typeof EvidenceConflictSchema>;

/**
 * `EvidenceBundle` 是"有什么、从哪来、覆盖到哪里、哪里矛盾"的完整回答，
 * **不含执行权限**：拿到 bundle 不能换取任何动作许可。
 */
export const EvidenceBundleSchema = z.object({
  bundleId: z.string().min(1).max(160),
  purpose: z.enum(["task_verification", "form_assistance", "information_collection"]),
  facts: z.array(EvidenceFactSchema).max(400).default([]),
  entities: z.array(EvidenceEntitySchema).max(200).default([]),
  sources: z.array(EvidenceSourceRefSchema).max(200).default([]),
  componentStates: z.array(EvidenceComponentStateSchema).max(40).default([]),
  coverage: EvidenceCoverageSchema,
  /** 目标市场口径：来自会话目标，不从页面猜。 */
  market: EvidenceMarketSchema.optional(),
  conflicts: z.array(EvidenceConflictSchema).max(40).default([]),
  createdAt: z.string(),
}).strict().superRefine((value, context) => {
  const sourceRefs = new Set(value.sources.map((source) => source.sourceRef));
  for (const [index, fact] of value.facts.entries()) {
    if (fact.sourceRef && !sourceRefs.has(fact.sourceRef)) {
      context.addIssue({ code: "custom", path: ["facts", index, "sourceRef"], message: "事实引用了不存在的来源" });
    }
  }
});
export type EvidenceBundle = z.infer<typeof EvidenceBundleSchema>;

/**
 * 出处的**来源地址**：由"读到这些内容的那一页"补上，逐字照抄。
 *
 * 为什么这条规则住在契约里、而不是各写一份：证据包有**两个生产者**——
 * 内容脚本的本地表达式（免费、无模型调用）与服务端的识别调用。
 * 两边各写一份"什么时候该填地址"，就会出现"一条路径有地址、另一条没有"的静默漂移；
 * 而下游（客户端的自动核实 `autoSourceOf`）**只从出处地址**推来源平台与来源国家。
 * 真机后果（2026-09-17）：两个生产者都没填 ⇒ 自动核实永远推不出来 ⇒
 * 5 个任务 × 4 条信号 `reviews` 全为 0 ⇒ 用户看到的结论从来没被保存过。
 *
 * 边界（宁可少填，不冒充）：
 * - 只给**页面派生**的位置填：`block`（页面文字块）与 `table_cell`（页面表格单元格）；
 * - `image_region`（截图区域）与 `user_input`（用户输入）**不**填——它们的出处不是这一页的地址；
 * - 已经有地址的出处一个字都不改（更具体的地址优先）。
 */
const PAGE_DERIVED_SOURCE_LOCATIONS: ReadonlySet<EvidenceLocation["kind"]> = new Set(["block", "table_cell"]);

/**
 * 截图区域也补地址，**但只在调用方明确说明"这张图就是这一页的"时候**。
 *
 * 截图路径原先不在补地址的范围内（它的出处不是"这一页上的某个单元格"）。真机后果很具体：
 * 四张初筛卡**全部走截图**，于是预算/竞争/价格带/评论的证据包里没有一个出处有地址 ⇒
 * 客户端的自动核实（`autoSourceOf` 只从出处地址推来源平台与国家）永远推不出来 ⇒
 * 结论从来不提交、卡片永远停在「待核实」——即使卡上已经把结论算出来显示给用户了。
 *
 * 为什么"照样是如实的"：截图就是**在那一页上**拍的，地址是这次观察的事实，与
 * "这条事实读自这一页的哪一块"是两件事。补的是 URL，**不补** `location`（区域仍是 `image_region`）。
 * 仍然要调用方显式打开（`includeScreenshot`），因为只有它知道这张图是不是这一页的截图。
 */
const SCREENSHOT_DERIVED_SOURCE_LOCATIONS: ReadonlySet<EvidenceLocation["kind"]> = new Set(["block", "table_cell", "image_region"]);

export function withObservedSourceUrls(
  bundle: EvidenceBundle,
  observationUrl: string | undefined,
  options: { includeScreenshot?: boolean } = {},
): EvidenceBundle {
  if (!observationUrl) return bundle;
  const fillable = options.includeScreenshot ? SCREENSHOT_DERIVED_SOURCE_LOCATIONS : PAGE_DERIVED_SOURCE_LOCATIONS;
  let changed = false;
  const sources = bundle.sources.map((source) => {
    if (source.sourceUrl) return source;
    if (!fillable.has(source.location.kind)) return source;
    changed = true;
    return { ...source, sourceUrl: observationUrl };
  });
  return changed ? { ...bundle, sources } : bundle;
}

/* -------------------------------------------------------------------------- */
/* §8.4 字段采用：采用 ≠ 确认执行                                                */
/* -------------------------------------------------------------------------- */

/**
 * `adopted` 只改插件草稿。这里刻意没有 "submit" / "publish" 之类的取值，
 * 也没有任何授权字段，"采用"在类型层就不可能等价于"执行"。
 */
export const FieldAdoptionSchema = z.object({
  fieldId: z.string().min(1).max(120),
  label: z.string().max(160).optional(),
  currentValue: z.string().max(2_000).optional(),
  candidateValue: z.string().max(2_000).optional(),
  sourceRef: z.string().min(1).max(160).optional(),
  precision: EvidencePrecisionSchema,
  conflictsWith: z.array(z.string().min(1).max(160)).max(8).default([]),
  decision: z.enum(["pending", "adopted", "modified", "rejected"]),
  /** 用户修改后的值；只有 modified 才允许出现。 */
  adoptedValue: z.string().max(2_000).optional(),
  confirmedAt: z.string().optional(),
  draftRevision: z.number().int().nonnegative(),
}).strict().superRefine((value, context) => {
  if (value.decision === "modified" && value.adoptedValue === undefined) {
    context.addIssue({ code: "custom", path: ["adoptedValue"], message: "修改后必须给出实际采用值" });
  }
  if (value.decision !== "modified" && value.adoptedValue !== undefined) {
    context.addIssue({ code: "custom", path: ["adoptedValue"], message: "只有修改才允许带 adoptedValue" });
  }
  if (value.decision === "adopted" && value.candidateValue === undefined) {
    context.addIssue({ code: "custom", path: ["candidateValue"], message: "采用必须有候选值" });
  }
  if ((value.decision === "adopted" || value.decision === "modified") && !value.confirmedAt) {
    context.addIssue({ code: "custom", path: ["confirmedAt"], message: "采用必须记录确认时间" });
  }
});
export type FieldAdoption = z.infer<typeof FieldAdoptionSchema>;

/* -------------------------------------------------------------------------- */
/* §8.1 CallReceipt：只有状态、用量与结算，装不下正文                            */
/* -------------------------------------------------------------------------- */

export const EvidenceCallStageSchema = z.enum([
  "capabilities",
  "plan",
  "plan_next",
  "plan_confirm",
  "action_authorize",
  "extraction",
  "screenshot_recognition",
  /** 链路（ExecutionRecipe）生成：同样是计费模型调用，且**不属于任何会话**。 */
  "recipe_ensure",
  /** P3-2：开放网络检索。它是**花钱的外部调用**，所以必须和模型调用一样留回执。 */
  "web_search",
  /** P3-41：把搜索词按目标市场语言翻译（用户确认「不是直接使用，而是需要翻译」）。 */
  "search_query",
]);
export const EvidenceCallStatusSchema = z.enum(["started", "succeeded", "failed", "rejected", "interrupted"]);
export type EvidenceCallStage = z.infer<typeof EvidenceCallStageSchema>;
export type EvidenceCallStatus = z.infer<typeof EvidenceCallStatusSchema>;

/** 刻意没有任何能装模型输入／输出的字段（§10.2、§11.2）。 */
export const EvidenceCallReceiptSchema = z.object({
  requestId: z.string().min(1).max(160),
  sessionId: z.string().min(1).max(160).optional(),
  protocol: z.literal("evidence-acquisition-v1"),
  stage: EvidenceCallStageSchema,
  status: EvidenceCallStatusSchema,
  model: z.string().max(120).optional(),
  promptVersion: z.string().max(80).optional(),
  promptHash: z.string().regex(/^[a-f\d]{64}$/).optional(),
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  /** 预扣的唯一标记：同一 run 的预扣与结算只能有一条。 */
  billingId: z.string().max(160).optional(),
  pointsCost: z.number().int().nonnegative().optional(),
  durationMs: z.number().int().nonnegative(),
  errorCode: z.string().max(80).optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
}).strict();
export type EvidenceCallReceipt = z.infer<typeof EvidenceCallReceiptSchema>;
